/**
 * Publish to Subscribers Hook - Incoming (Terminal)
 *
 * Routes message to subscription manager for delivery to subscribers.
 * This is a terminal hook - runs last, publishes to all matching subscribers.
 */

import { BaseHook } from '../base/BaseHook';
import type { IPipelineContext, HookResult } from '../../types';
import type { ISubscriptionManager } from '../../types/subscription.types';

export class PublishToSubscribersHook extends BaseHook {
  readonly name = 'PublishToSubscribersHook';

  readonly priority = 200; // Terminal hook - runs last

  private subscriptionManager: ISubscriptionManager | null = null;

  constructor(subscriptionManager?: ISubscriptionManager) {
    super();
    this.subscriptionManager = subscriptionManager || null;
  }

  /**
   * Set subscription manager (used when creating hook in pipeline builder)
   */
  setSubscriptionManager(manager: ISubscriptionManager): void {
    this.subscriptionManager = manager;
  }

  async execute(context: IPipelineContext): Promise<HookResult> {
    if (!this.subscriptionManager) {
      this.logger.warn('No subscription manager configured');
      return this.continue();
    }

    try {
      const topic = context.envelope.meta.topic || 'default';
      const messageId = context.envelope.meta.id;

      // Publish to all matching subscribers
      await this.subscriptionManager.publish(topic, context.envelope, messageId);

      this.logger.debug(`Published to subscribers on topic "${topic}"`);
      return this.continue();
    } catch (error) {
      return this.error(`Publish failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
}
